<script lang="ts" setup>
import type { ErrorObject } from '@vuelidate/core';

import { Button } from '~ds/components/ui/button';
import { AnimatePresence, motion } from 'motion-v';

import type { CountryCodeAlpha2 } from '~/types/core';

import CorePhoneNumber from '~/components/Core/CorePhoneNumber.vue';
import { useVerification } from '~/composables/Verification/useVerification';
import { VERIFICATION_STEPS, VERIFICATION_TYPES } from '~/types/verification';

interface Props {
  label: string;
  disabled?: boolean;
  required?: boolean;
  baseCountry?: CountryCodeAlpha2;
  version?: 'v1' | 'v2';
}

withDefaults(defineProps<Props>(), {
  disabled: false,
  required: false,
  baseCountry: undefined,
  version: 'v1',
});

const emit = defineEmits<{
  'valid': [isValid: boolean];
  'keydown.enter': [];
  'verified': [];
}>();

const phoneNumber = defineModel<string>({ required: true });
const { t } = useI18n();

const {
  authMethodValue,
  pincodeValue,
  currentStep,
  loading,
  handleSubmit,
  handleRequestVerificationCode,
  handleWrongAuthMethodEntered,
  handleVerificationSuccessReset,
  errorMessage,
  v$,
  handlePhoneIsValid,
  isSubmitDisabled,
} = useVerification(VERIFICATION_TYPES.PHONE, event => emit(event), phoneNumber.value);

watch(authMethodValue, (v) => {
  phoneNumber.value = v;
});

watch(currentStep, (step) => {
  if (step === VERIFICATION_STEPS.SUCCESS) {
    emit('verified');
    handleVerificationSuccessReset();
  }
});

watch(pincodeValue, (v) => {
  if (v.length === 6) {
    handleSubmit();
  }
});

const handleValid = (isValid: boolean) => {
  handlePhoneIsValid(isValid);
  emit('valid', isValid);
};

const inputErrorMessages = computed(() => {
  if (errorMessage.value) {
    return [errorMessage.value];
  }
  return v$.value.pincodeValue?.$errors?.map((e: ErrorObject) => String(e.$message)) ?? [];
});
</script>

<!-- eslint-disable vue/custom-event-name-casing -->
<template>
  <div>
    <AnimatePresence mode="wait">
      <motion.div
        v-if="currentStep === VERIFICATION_STEPS.AUTH_METHOD_INPUT"
        key="input"
        :initial="{ opacity: 0, x: -16 }"
        :animate="{ opacity: 1, x: 0 }"
        :exit="{ opacity: 0, x: -16 }"
        :transition="{ duration: 0.2 }"
      >
        <KUICoreInputLabel :label="label" :required="required" />
        <div class="mt-1 flex items-center gap-3">
          <CorePhoneNumber
            v-model="authMethodValue"
            :label="label"
            hide-label
            :disabled="disabled"
            :required="required"
            :verified="disabled"
            :base-country="baseCountry"
            :version="version"
            class="flex-1 min-w-0"
            @valid="handleValid"
            @keydown.enter="emit('keydown.enter')"
          />
          <Button
            v-if="!disabled"
            variant="outline"
            size="sm"
            :loading="loading"
            :disabled="isSubmitDisabled"
            type="button"
            @click="handleSubmit"
          >
            {{ $t('unified.account.phoneField.saveAndVerify') }}
          </Button>
        </div>
      </motion.div>
      <motion.div
        v-else-if="currentStep === VERIFICATION_STEPS.PINCODE_INPUT"
        key="otp"
        :initial="{ opacity: 0, x: 16 }"
        :animate="{ opacity: 1, x: 0 }"
        :exit="{ opacity: 0, x: 16 }"
        :transition="{ duration: 0.2 }"
        class="bg-background px-4 py-2 rounded-3xl"
      >
        <KUICorePinCode
          v-model="pincodeValue"
          :label="t('general.formFields.otpCode.label')"
          focus-on-mounted
          :disabled="loading"
        />
        <div class="mt-3 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            :disabled="loading"
            class="font-normal"
            @click="handleWrongAuthMethodEntered"
          >
            {{ $t('unified.account.phoneField.changePhoneNumber') }}
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            :loading="loading"
            :disabled="loading"
            class="font-normal"
            @click="handleRequestVerificationCode"
          >
            {{ $t('general.formFields.resendCode.label') }}
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
    <KUICoreInputErrors
      v-if="inputErrorMessages.length"
      class="mt-2"
      :error-messages="inputErrorMessages"
    />
  </div>
</template>
